import { ServicePlugin } from '@91app/dc-sdk';
import type { BaseEvent, PluginReadyParams } from '@91app/dc-sdk/types';
import type {
  AddDataStoreMappingEvent,
  DataStoreMapping,
  SessionPayload,
} from './types';
import { DataStoreSubscriptionService } from './services/dataStoreSubscription';
import { convertValue } from './utils/typeConverter';

/**
 * Session Service Plugin
 * 負責 sessionStorage 與 dataStore 之間的雙向同步
 */
export default class SessionServicePlugin extends ServicePlugin {
  private dataStoreMappings: DataStoreMapping[] = [];
  private dataStoreSubscriptionService: DataStoreSubscriptionService;

  constructor(platformContext: PluginReadyParams) {
    super(platformContext);

    const { isInitialSetup, dataStoreMappings } =
      (platformContext?.payload as SessionPayload) || {};
    this.dataStoreMappings = dataStoreMappings || [];

    this.dataStoreSubscriptionService = new DataStoreSubscriptionService(
      this.dataStore,
    );

    this.initUtmParams();

    // 初始化時同步 sessionStorage 到 DataStore
    if (isInitialSetup) {
      this.syncSessionStorageToDataStore();
    }

    // 始終訂閱 DataStore 變化（只要有 mappings）
    if (this.dataStoreMappings.length > 0) {
      this.dataStoreSubscriptionService.subscribeDataStore(
        this.dataStoreMappings,
      );
    }

    this.publishReady();
  }

  onSubscribedCommandEvent(event: BaseEvent, $replyChannel?: string): void {
    // $replyChannel is available for reply-based communication but not used in this plugin
    if (event.type === 'ADD_DATA_STORE_MAPPINGS') {
      const eventData = event as AddDataStoreMappingEvent;
      const mappings = eventData.$dp.dataStoreMappings || [];

      this.dataStoreMappings = this.deduplicateMappings([
        ...this.dataStoreMappings,
        ...mappings,
      ]);
      this.dataStoreSubscriptionService.unsubscribeAll();
      this.syncSessionStorageToDataStore();
      this.dataStoreSubscriptionService.subscribeDataStore(
        this.dataStoreMappings,
      );

      if (eventData.$dp.replyChannel) {
        this.eventEmitter.sendTo(eventData.$dp.replyChannel, {
          type: 'ADD_DATA_STORE_MAPPINGS_DONE',
        });
      }
    }
  }
  onSubscribedReplyEvent(event: BaseEvent): void {
    // Reply events are currently not handled
  }
  onSubscribedExternalEvent(channel: string, event: BaseEvent): void {
    // External events are currently not handled
  }
  onDestroy(): void {
    this.dataStoreSubscriptionService.unsubscribeAll();
  }

  /**
   * 從 URL query string 讀取 UTM 參數寫入 sessionStorage（first-touch）
   * sessionStorage 已有值時不覆蓋，確保 session 內的歸因來源以第一次進站為準
   */
  private initUtmParams(): void {
    const params = new URLSearchParams(window.location.search);
    const utmKeyMap: Record<string, string> = {
      utm_source: 'utmSource',
      utm_medium: 'utmMedium',
      utm_campaign: 'utmCampaign',
      utm_term: 'utmTerm',
      utm_content: 'utmContent',
      tripid: 'tripid',
    };

    for (const [urlKey, sessionKey] of Object.entries(utmKeyMap)) {
      const value = params.get(urlKey);
      if (!value) continue;
      try {
        if (window.sessionStorage.getItem(sessionKey) === null) {
          window.sessionStorage.setItem(sessionKey, value);
        }
      } catch {
        // Silently handle storage access errors (e.g., storage disabled, quota exceeded)
      }
    }
  }

  /**
   * 去重 mappings
   * 使用 Map 去重，key 為 dataStorePath + sessionKey，新值會覆蓋舊值
   *
   * @param mappings
   * @returns DataStoreMapping[]
   */
  private deduplicateMappings(
    mappings: DataStoreMapping[],
  ): DataStoreMapping[] {
    const mappingMap = new Map<string, DataStoreMapping>();
    for (const mapping of mappings) {
      if (!mapping.dataStorePath || !mapping.sessionKey) {
        continue;
      }
      const key = `${mapping.dataStorePath}:${mapping.sessionKey}`;
      mappingMap.set(key, mapping);
    }
    return Array.from(mappingMap.values());
  }

  /**
   * 同步 sessionStorage 到 DataStore
   *
   * @returns void
   */
  private syncSessionStorageToDataStore(): void {
    for (const mapping of this.dataStoreMappings) {
      if (!mapping.sessionKey || !mapping.dataStorePath) {
        continue;
      }

      try {
        const value = window.sessionStorage.getItem(mapping.sessionKey);
        if (value !== null) {
          const convertedValue = convertValue(value, mapping.type);
          this.dataStore?.set(mapping.dataStorePath, convertedValue);
        } else if (mapping.defaultValue !== undefined) {
          const convertedValue = convertValue(
            mapping.defaultValue,
            mapping.type,
          );
          this.dataStore?.set(mapping.dataStorePath, convertedValue);
        }
      } catch (error) {
        // Silently handle storage access errors (e.g., storage disabled, quota exceeded)
        // Errors are ignored to prevent breaking other mappings
      }
    }
  }
}
